import React from 'react';
import { Text, StyleSheet, View, Link } from '@react-pdf/renderer';
import { SerializedContent } from '@/features/screenplay/components/editor/utils/JsonUtils';
import { BasePDFDocument } from '@/shared/components/pdf/BasePDFDocument';

interface StoryPDFTemplateProps {
    blocks: SerializedContent[];
    brandLogoBase64?: string;
    projectTitle?: string;
    authorName?: string;
}

const styles = StyleSheet.create({
    contentContainer: {
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 40,
    },
    titlePage: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    projectTitle: {
        fontSize: 32,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 12,
        color: '#1a202c',
    },
    authorName: {
        fontSize: 18,
        textAlign: 'center',
        color: '#4a5568',
        marginBottom: 40,
    },
    metaInfo: {
        fontSize: 10,
        color: '#a0aec0',
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    paragraph: {
        fontSize: 12,
        lineHeight: 1.6,
        marginBottom: 12,
        textAlign: 'justify',
        color: '#2d3748',
        fontFamily: 'Helvetica',
    },
    heading1: {
        fontSize: 20,
        fontWeight: 'bold',
        marginTop: 24,
        marginBottom: 12,
        color: '#1a202c',
        borderBottomWidth: 1,
        borderBottomColor: '#e2e8f0',
        paddingBottom: 4,
        lineHeight: 1.2,
        textAlign: 'left',
        fontFamily: 'Helvetica-Bold',
    },
    heading2: {
        fontSize: 16,
        fontWeight: 'bold',
        marginTop: 20,
        marginBottom: 8,
        color: '#2d3748',
        lineHeight: 1.2,
        textAlign: 'left',
        fontFamily: 'Helvetica-Bold',
    },
    pageBreak: {
        marginBottom: 20,
    }
});

export const StoryPDFTemplate: React.FC<StoryPDFTemplateProps> = ({
    blocks,
    brandLogoBase64,
    projectTitle,
    authorName
}) => {
    return (
        <BasePDFDocument brandLogoBase64={brandLogoBase64}>
            {/* ── Title Section (Initial Content) ── */}
            <View style={styles.titlePage}>
                <Text style={styles.projectTitle}>{projectTitle || 'Untitled Story'}</Text>
                {authorName && <Text style={styles.authorName}>by {authorName}</Text>}
                <Text style={styles.metaInfo}>Story Document</Text>
            </View>

            {/* ── Story Content ── */}
            <View style={styles.contentContainer}>
                {blocks.map((block) => {
                    const text = block.children?.map(c => c.text).join('') || '';

                    if (block.type === 'page-break') {
                        return <View key={block.id} break style={styles.pageBreak} />;
                    }

                    if (!text.trim()) {
                        return <View key={block.id} style={{ height: 12 }} />; // Empty line spacer
                    }

                    let style: any = styles.paragraph;
                    switch (block.type) {
                        case 'h1':
                        case 'heading1':
                            style = styles.heading1;
                            break;
                        case 'h2':
                        case 'heading2':
                            style = styles.heading2;
                            break;
                        default:
                            style = styles.paragraph;
                            break;
                    }

                    return (
                        <Text key={block.id} style={style}>
                            {text}
                        </Text>
                    );
                })}
            </View>
        </BasePDFDocument>
    );
};
